import { useEffect, useState } from "react";
import {
  createProduct, deleteProduct, fetchOrders, fetchProducts, formatINR, updateProduct,
  fetchReviews, updateReview, deleteReview,
  getSetting, setSetting,
  adminLogin,
  resolveMediaUrl,
  adminUpdateOrderStatus, adminDeleteOrder, adminReorderProducts,
  adminListCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon,
  adminListReissueRequests, adminUpdateReissue, adminDeleteReissue,
  adminListBundleOffers, adminCreateBundleOffer, adminUpdateBundleOffer, adminDeleteBundleOffer,
} from "../lib/api";
import { Plus, Trash2, Edit3, X, Star, Eye, EyeOff, Lock, GripVertical, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import MediaUploader from "../components/MediaUploader";
import RichTextEditor from "../components/RichTextEditor";
import { api } from "../lib/api";

const CATEGORIES = ["Earrings", "Necklaces", "Sets", "Bracelets", "Rings"];
const COLLECTIONS = ["Daily Luxe", "Date Glam", "9 to 5", "Party Edit", "Big Moments", "Gifts for Her"];
const COLORS = ["Gold", "Silver", "Pearl", "Rose", "Green", "Champagne", "Purple", "Black"];
const SINGULAR_LABELS = ["Sole Edition", "Original Piece", "Not Reproduced", "Signature Piece", "Archive No. 001"];

const empty = {
  name: "", description: "", price: 0, mrp: "", sku: "", category: "Earrings",
  collections: [], colors: [], images: [], videos: [], is_featured: false,
  stock_quantity: 1, is_singular: true, singular_label: "Sole Edition",
  sort_order: "",
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("hb_admin_token"));

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;
  return <AdminPanel onLogout={() => { sessionStorage.removeItem("hb_admin_token"); setAuthed(false); }} />;
}

/* ---------- Login gate ---------- */

function AdminLogin({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminLogin(pw);
      sessionStorage.setItem("hb_admin_token", r.token);
      onSuccess();
    } catch {
      toast.error("Invalid password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="bg-[#0A0A0A] min-h-screen pt-[100px] flex items-start justify-center" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm mx-auto px-6 py-12 text-center">
        <Lock size={24} strokeWidth={1.5} className="mx-auto" style={{ color: "#F5D565" }} />
        <h1 className="font-serif-display mt-5" style={{ fontSize: 36, color: "#FFFFFF", fontWeight: 500 }}>
          Admin access
        </h1>
        <p className="font-body mt-3" style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
          Enter the admin password to continue.
        </p>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          data-testid="admin-password-input"
          className="w-full mt-8 bg-transparent border-b border-[#262626] focus:border-[#F5D565] outline-none font-body text-center"
          style={{ fontSize: 16, padding: "12px 0", color: "#FFFFFF", letterSpacing: "0.1em" }}
          autoFocus
        />
        <button type="submit" disabled={busy} className="btn-gold mt-8 w-full" data-testid="admin-password-submit">
          {busy ? "Checking…" : "Enter Studio"}
        </button>
      </form>
    </main>
  );
}

/* ---------- Panel ---------- */

function AdminPanel({ onLogout }) {
  const [tab, setTab] = useState("products");
  return (
    <main data-testid="admin-page" className="bg-[#0A0A0A] min-h-screen pt-[100px] pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div
              className="font-ui mb-3"
              style={{ fontSize: 12, fontWeight: 600, color: "#F5D565", letterSpacing: "0.4em", textTransform: "uppercase" }}
            >
              ✦ Admin
            </div>
            <h1 className="font-serif-display" style={{ fontSize: 48, color: "#FFFFFF", fontWeight: 500 }}>Studio</h1>
          </div>
          <button onClick={onLogout} className="text-xs text-[#A3A3A3] hover:text-[#F5D565]" data-testid="admin-logout">Sign out</button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-[#262626] overflow-x-auto no-scrollbar">
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="tab-products">Products</TabBtn>
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">Orders</TabBtn>
          <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")} testid="tab-reviews">Manage Reviews</TabBtn>
          <TabBtn active={tab === "page"} onClick={() => setTab("page")} testid="tab-page">Page Control</TabBtn>
          <TabBtn active={tab === "filters"} onClick={() => setTab("filters")} testid="tab-filters">Filter Manager</TabBtn>
          <TabBtn active={tab === "sort"} onClick={() => setTab("sort")} testid="tab-sort">Sort Manager</TabBtn>
          <TabBtn active={tab === "coupons"} onClick={() => setTab("coupons")} testid="tab-coupons">Coupons</TabBtn>
          <TabBtn active={tab === "reissue"} onClick={() => setTab("reissue")} testid="tab-reissue">Reissue Requests</TabBtn>
          <TabBtn active={tab === "bundles"} onClick={() => setTab("bundles")} testid="tab-bundles">Bundle Offers</TabBtn>
          <TabBtn active={tab === "policies"} onClick={() => setTab("policies")} testid="tab-policies">Policies</TabBtn>
        </div>

        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "page" && <PageControlTab />}
        {tab === "filters" && <FilterManagerTab />}
        {tab === "sort" && <SortManagerTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "reissue" && <ReissueTab />}
        {tab === "bundles" && <BundleOffersTab />}
        {tab === "policies" && <PoliciesTab />}
      </div>
    </main>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="font-ui transition-colors"
    style={{
      padding: "14px 20px",
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      borderBottom: active ? "2px solid #F5D565" : "2px solid transparent",
      color: active ? "#F5D565" : "rgba(255,255,255,0.7)",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

/* ---------- Products ---------- */

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () => fetchProducts({}).then(setProducts);
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, description: p.description, price: p.price,
      mrp: p.mrp || "",
      sku: p.sku || "",
      category: p.category,
      collections: p.collections || [], colors: p.colors || [],
      images: p.images?.length ? p.images : [], videos: p.videos || [],
      is_featured: !!p.is_featured,
      stock_quantity: typeof p.stock_quantity === "number" ? p.stock_quantity : 1,
      is_singular: p.is_singular !== false,
      singular_label: p.singular_label || "Sole Edition",
      sort_order: p.sort_order ?? "",
    });
    setShowForm(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, price: Number(form.price),
      mrp: form.mrp === "" || form.mrp === null ? null : Number(form.mrp),
      sku: (form.sku || "").trim(),
      sort_order: form.sort_order === "" || form.sort_order === null ? null : Number(form.sort_order),
      images: form.images.filter(Boolean),
      videos: (form.videos || []).filter(Boolean),
    };
    try {
      if (editing) { await updateProduct(editing, payload); toast.success("Product updated"); }
      else { await createProduct(payload); toast.success("Product created"); }
      setShowForm(false);
      load();
    } catch { toast.error("Failed to save"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await deleteProduct(id);
    toast.success("Product deleted");
    load();
  };
  const toggleColl = (c) =>
    setForm((f) => ({ ...f, collections: f.collections.includes(c) ? f.collections.filter((x) => x !== c) : [...f.collections, c] }));

  return (
    <div className="mt-8">
      <button onClick={startNew} className="btn-gold mb-6" data-testid="admin-new-product">
        <Plus size={14} /> New Product
      </button>
      <div className="overflow-x-auto border border-[#262626]">
        <table className="w-full text-sm" data-testid="products-table">
          <thead className="bg-[#141414] font-ui" style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
            <tr>
              <th className="text-left p-3" style={{ width: 90 }}>Rank</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Price</th>
              <th className="text-left p-3">MRP</th>
              <th className="text-left p-3">Stock</th>
              <th className="text-left p-3">Featured</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="font-body" style={{ fontSize: 14 }}>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[#1a1a1a]">
                <td className="p-3">
                  <RankInput
                    productId={p.id}
                    initial={p.sort_order}
                    onSaved={load}
                  />
                </td>
                <td className="p-3 flex items-center gap-3">
                  <img src={resolveMediaUrl(p.images?.[0])} alt="" className="w-10 h-12 object-cover" />
                  <span className="font-serif-display" style={{ fontSize: 16 }}>{p.name}</span>
                </td>
                <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>{p.category}</td>
                <td className="p-3">{formatINR(p.price)}</td>
                <td className="p-3" style={{ color: p.mrp && p.mrp > p.price ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)" }}>
                  {p.mrp ? formatINR(p.mrp) : "—"}
                </td>
                <td className="p-3" style={{ color: (p.stock_quantity || 0) <= 0 ? "#e67474" : "rgba(255,255,255,0.85)" }}>
                  {(p.stock_quantity || 0) <= 0 ? "Sold Out" : p.stock_quantity}
                </td>
                <td className="p-3">{p.is_featured ? "★" : "—"}</td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => startEdit(p)} className="hover:text-[#F5D565]" data-testid={`edit-${p.id}`}><Edit3 size={16} strokeWidth={1.5} /></button>
                  <button onClick={() => remove(p.id)} className="hover:text-red-400" data-testid={`delete-${p.id}`}><Trash2 size={16} strokeWidth={1.5} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <form onSubmit={submit} data-testid="product-form"
            className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8 relative">
            <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]"><X size={20} /></button>
            <h2 className="font-serif-display mb-6" style={{ fontSize: 28, color: "#FFFFFF" }}>{editing ? "Edit" : "New"} Product</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name *" v={form.name} on={(v) => setForm((f) => ({ ...f, name: v }))} full />
              <Field label="Price (₹) *" type="number" v={form.price} on={(v) => setForm((f) => ({ ...f, price: v }))} />
              <Field label="MRP / Compare-at (₹)" type="number" v={form.mrp} on={(v) => setForm((f) => ({ ...f, mrp: v }))} />
              <Field label="SKU" v={form.sku} on={(v) => setForm((f) => ({ ...f, sku: v }))} />
              <Field label="Rank (blank = end)" type="number" v={form.sort_order} on={(v) => setForm((f) => ({ ...f, sort_order: v }))} />
              <label className="block">
                <span className="font-ui" style={{ fontSize: 11, fontWeight: 600, color: "#F5D565", letterSpacing: "0.2em", textTransform: "uppercase" }}>Category *</span>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none" style={{ fontSize: 15, color: "#FFFFFF" }}>
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0A0A0A]">{c}</option>)}
                </select>
              </label>
              <Field label="Description" v={form.description} on={(v) => setForm((f) => ({ ...f, description: v }))} full textarea />
              <div className="md:col-span-2">
                <Label>Collections</Label>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {COLLECTIONS.map((c) => <button key={c} type="button" onClick={() => toggleColl(c)} className={`pill ${form.collections.includes(c) ? "active" : ""}`}>{c}</button>)}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Colours</Label>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {COLORS.map((c) => <button key={c} type="button"
                    onClick={() => setForm((f) => ({ ...f, colors: f.colors.includes(c) ? f.colors.filter((x) => x !== c) : [...f.colors, c] }))}
                    className={`pill ${form.colors.includes(c) ? "active" : ""}`}>{c}</button>)}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Product Images</Label>
                <MediaUploader items={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} mode="image" />
              </div>
              <div className="md:col-span-2">
                <Label>Product Videos (optional)</Label>
                <MediaUploader items={form.videos} onChange={(vids) => setForm((f) => ({ ...f, videos: vids }))} mode="video" />
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))} className="accent-[#F5D565]" />
                <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>Featured on home</span>
              </label>
              <Field label="Stock Quantity" type="number" v={form.stock_quantity} on={(v) => setForm((f) => ({ ...f, stock_quantity: Number(v) || 0 }))} />
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.is_singular} onChange={(e) => setForm((f) => ({ ...f, is_singular: e.target.checked }))} className="accent-[#F5D565]" data-testid="product-is-singular" />
                <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>Mark as Singular Piece</span>
              </label>
              {form.is_singular && (
                <label className="block md:col-span-2">
                  <Label>Singular Piece Label</Label>
                  <select value={form.singular_label} onChange={(e) => setForm((f) => ({ ...f, singular_label: e.target.value }))}
                          className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none"
                          style={{ fontSize: 15, color: "#FFFFFF" }}
                          data-testid="product-singular-label">
                    {SINGULAR_LABELS.map((l) => <option key={l} value={l} className="bg-[#0A0A0A]">{l}</option>)}
                  </select>
                </label>
              )}
            </div>
            <button type="submit" className="btn-gold mt-8 w-full">{editing ? "Update" : "Create"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---------- Orders ---------- */

const PENDING_STATUSES = ["placed", "confirmed", "out_for_delivery"];
const COMPLETED_STATUSES = ["delivered", "cancelled", "completed"];

const ORDER_STATUS_OPTIONS = [
  { value: "placed", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(null);
  const [view, setView] = useState("pending"); // pending | completed
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const load = () => fetchOrders().then(setOrders);
  useEffect(() => { load(); }, []);

  const pendingCount = orders.filter((o) => PENDING_STATUSES.includes(o.order_status)).length;
  const completedCount = orders.filter((o) => COMPLETED_STATUSES.includes(o.order_status)).length;
  const filtered = orders.filter((o) =>
    view === "pending"
      ? PENDING_STATUSES.includes(o.order_status)
      : COMPLETED_STATUSES.includes(o.order_status)
  );

  const changeStatus = async (orderId, newStatus) => {
    setSaving(orderId);
    try {
      await adminUpdateOrderStatus(orderId, newStatus);
      const isEmail = ["confirmed", "out_for_delivery", "delivered", "cancelled"].includes(newStatus);
      toast.success(isEmail ? "Status updated. Customer email sent." : "Status updated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status");
    } finally {
      setSaving(null);
    }
  };

  const markCompleted = (orderId) => changeStatus(orderId, "completed");

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    try {
      await adminDeleteOrder(deleteTarget.id);
      toast.success(`Deleted #${deleteTarget.order_number}`);
      setDeleteTarget(null);
      setDeleteText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div className="mt-8" data-testid="orders-tab">
      {/* Sub-tabs */}
      <div className="flex gap-0 border border-[#262626] mb-6">
        <SubTab active={view === "pending"} onClick={() => setView("pending")} testid="orders-tab-pending">
          Pending ({pendingCount})
        </SubTab>
        <SubTab active={view === "completed"} onClick={() => setView("completed")} testid="orders-tab-completed">
          Completed ({completedCount})
        </SubTab>
      </div>

      <div className="space-y-4" data-testid="orders-list">
        {filtered.length === 0 ? (
          <p className="font-serif-display italic" style={{ fontSize: 24, color: "rgba(255,255,255,0.6)" }}>
            {view === "pending" ? "No pending orders." : "No completed orders yet."}
          </p>
        ) : filtered.map((o) => {
          const isPending = PENDING_STATUSES.includes(o.order_status);
          return (
            <div key={o.id} className="border border-[#262626] p-5" data-testid={`admin-order-${o.order_number}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-serif-display" style={{ fontSize: 20, color: "#F5D565" }}>#{o.order_number}</div>
                  <div className="font-body mt-1" style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                    {o.address.full_name} · {o.address.city} · {o.payment_method.toUpperCase()}
                  </div>
                  <div className="font-body mt-1" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                    {o.address.email} · {o.address.phone}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif-display" style={{ fontSize: 24, color: "#FFFFFF" }}>{formatINR(o.total)}</div>
                  <div className="font-ui" style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
                    {o.payment_status}
                  </div>
                </div>
              </div>
              <div className="font-body mt-3" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                {o.items.map((i) => `${i.name} x${i.quantity}`).join(" · ")}
              </div>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className="font-ui" style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>Status:</span>
                <select
                  value={o.order_status}
                  onChange={(e) => changeStatus(o.id, e.target.value)}
                  disabled={saving === o.id || !isPending}
                  data-testid={`admin-order-status-${o.order_number}`}
                  className="bg-transparent border border-[#262626] py-1.5 px-2 text-sm focus:border-[#F5D565] outline-none font-body disabled:opacity-50"
                  style={{ color: "#FFFFFF" }}
                >
                  {ORDER_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value} className="bg-[#0A0A0A]">{s.label}</option>
                  ))}
                </select>
                {isPending && (
                  <button
                    onClick={() => markCompleted(o.id)}
                    disabled={saving === o.id}
                    data-testid={`admin-mark-completed-${o.order_number}`}
                    className="font-ui inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{
                      fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
                      padding: "6px 10px", color: "#0A0A0A", background: "#F5D565", border: "1px solid #F5D565",
                    }}
                  >
                    <CheckCircle2 size={12} strokeWidth={2} /> Mark Completed
                  </button>
                )}
                <button
                  onClick={() => { setDeleteTarget(o); setDeleteText(""); }}
                  data-testid={`admin-delete-order-${o.order_number}`}
                  className="font-ui inline-flex items-center gap-1.5 ml-auto"
                  style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
                    padding: "6px 10px", color: "#FF9B9B", border: "1px solid #FF9B9B",
                  }}
                >
                  <Trash2 size={12} strokeWidth={1.75} /> Delete
                </button>
                {saving === o.id && <span className="text-[11px] text-[#F5D565]">Saving…</span>}
                {o.cancel_reason && (
                  <span className="text-[11px] text-[#A3A3A3]">Reason: <span style={{ color: "rgba(255,255,255,0.85)" }}>{o.cancel_reason}</span></span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="delete-order-dialog">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md p-6 md:p-7 relative">
            <button onClick={() => { setDeleteTarget(null); setDeleteText(""); }} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]"><X size={18} /></button>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.4em] uppercase" style={{ color: "#FF9B9B" }}>
              <AlertTriangle size={14} /> Hard delete
            </div>
            <h2 className="font-serif-display mt-3" style={{ fontSize: 22, color: "#FFFFFF" }}>
              Delete #{deleteTarget.order_number}?
            </h2>
            <p className="font-body mt-2" style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.55 }}>
              This permanently removes the order from the database. Type <strong className="text-[#F5D565]">DELETE</strong> to confirm.
            </p>
            <input
              autoFocus
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              data-testid="delete-confirm-input"
              placeholder="Type DELETE"
              className="mt-5 w-full bg-transparent border border-[#262626] focus:border-[#FF9B9B] outline-none px-3 py-2.5 text-sm font-ui uppercase tracking-[0.2em]"
              style={{ color: "#FFFFFF" }}
            />
            <div className="mt-6 flex gap-3">
              <button onClick={() => { setDeleteTarget(null); setDeleteText(""); }}
                className="flex-1 border border-[#262626] py-3 font-ui"
                style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={deleteText !== "DELETE"}
                className="flex-1 py-3 font-ui disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  fontSize: 12, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
                  color: "#0A0A0A", background: "#FF9B9B",
                }}
                data-testid="delete-confirm"
              >
                Yes, delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SubTab = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="flex-1 font-ui transition-all"
    style={{
      padding: "12px 14px",
      fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
      color: active ? "#F5D565" : "rgba(255,255,255,0.6)",
      background: active ? "rgba(245,213,101,0.06)" : "transparent",
      borderBottom: active ? "2px solid #F5D565" : "2px solid transparent",
    }}
  >
    {children}
  </button>
);

/* ---------- Manage Reviews ---------- */

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchReviews({ limit: 200 }).then(setReviews).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleFeatured = async (r) => {
    try {
      await updateReview(r.id, { featured: !r.featured });
      toast.success(!r.featured ? "Now featured on website" : "Removed from website");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    await deleteReview(r.id);
    toast.success("Review deleted");
    load();
  };

  const featuredCount = reviews.filter((r) => r.featured).length;

  return (
    <div className="mt-8" data-testid="manage-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <p className="font-body" style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
          Showing {reviews.length} review{reviews.length === 1 ? "" : "s"} · {featuredCount}/3 featured on website
        </p>
      </div>
      {loading ? (
        <p className="font-body" style={{ color: "rgba(255,255,255,0.5)" }}>Loading…</p>
      ) : reviews.length === 0 ? (
        <p className="font-serif-display italic" style={{ fontSize: 20, color: "rgba(255,255,255,0.6)" }}>No reviews submitted yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <div key={r.id} className="border p-5" style={{ borderColor: r.featured ? "#F5D565" : "#262626", background: r.featured ? "rgba(245,213,101,0.04)" : "transparent" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={12} fill={i < (r.rating || 5) ? "#F5D565" : "transparent"} stroke="#F5D565" />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleFeatured(r)}
                    className="font-ui inline-flex items-center gap-1.5 transition-colors"
                    data-testid={`toggle-feature-${r.id}`}
                    style={{
                      fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", textTransform: "uppercase",
                      padding: "6px 10px",
                      color: r.featured ? "#0A0A0A" : "#F5D565",
                      background: r.featured ? "#F5D565" : "transparent",
                      border: "1px solid #F5D565",
                    }}
                  >
                    {r.featured ? (<><EyeOff size={11} strokeWidth={1.75} /> Hide</>) : (<><Eye size={11} strokeWidth={1.75} /> Show on site</>)}
                  </button>
                  <button onClick={() => remove(r)} className="text-[#A3A3A3] hover:text-red-400" data-testid={`delete-review-${r.id}`}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <p className="font-body mt-3" style={{ fontSize: 15, color: "rgba(255,255,255,0.88)", lineHeight: 1.55 }}>"{r.text}"</p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="font-ui" style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 600 }}>{r.name}</span>
                {r.city && <span className="font-body" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>· {r.city}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Page Control: hero images + home videos ---------- */

function PageControlTab() {
  const [heroImages, setHeroImages] = useState([]);
  const [homeVideos, setHomeVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getSetting("hero_images"), getSetting("home_videos")]).then(([hi, hv]) => {
      setHeroImages(Array.isArray(hi?.value) ? hi.value : []);
      setHomeVideos(Array.isArray(hv?.value) ? hv.value : []);
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await setSetting("hero_images", heroImages.filter(Boolean).slice(0, 5));
      await setSetting("home_videos", homeVideos.filter(Boolean).slice(0, 3));
      toast.success("Saved. Reload the homepage to see changes.");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="font-body mt-8" style={{ color: "rgba(255,255,255,0.5)" }}>Loading…</p>;

  return (
    <div className="mt-8 space-y-10" data-testid="page-control">
      <section>
        <h3 className="font-serif-display" style={{ fontSize: 24, color: "#FFFFFF" }}>Hero Carousel Images</h3>
        <p className="font-body mt-2" style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
          Up to 5 images. Shown full-width at the top of the homepage. Leave empty to use default set.
        </p>
        <MediaUploader items={heroImages} onChange={setHeroImages} mode="image" />
      </section>

      <section>
        <h3 className="font-serif-display" style={{ fontSize: 24, color: "#FFFFFF" }}>Home Videos (bottom 3-tile row)</h3>
        <p className="font-body mt-2" style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
          Exactly 3 short clips. Shown as auto-playing muted loops. Leave empty to use default set.
        </p>
        <MediaUploader items={homeVideos} onChange={setHomeVideos} mode="video" />
      </section>

      <button onClick={save} disabled={saving} className="btn-gold" data-testid="page-control-save">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

/* ---------- Filter Manager: home category + mood pills ---------- */

function FilterManagerTab() {
  const [categories, setCategories] = useState(null);
  const [moods, setMoods] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getSetting("home_categories"), getSetting("home_moods")]).then(([c, m]) => {
      setCategories(Array.isArray(c?.value) ? c.value : []);
      setMoods(Array.isArray(m?.value) ? m.value : []);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await setSetting("home_categories", categories);
      await setSetting("home_moods", moods);
      toast.success("Saved. Reload the homepage to see changes.");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!categories || !moods) {
    return <p className="font-body mt-8" style={{ color: "rgba(255,255,255,0.5)" }}>Loading…</p>;
  }

  return (
    <div className="mt-8 space-y-10" data-testid="filter-manager">
      <FilterGroup
        title="Row 1 — Categories"
        subtitle="Shown on the homepage top row. Drag the arrows to reorder. Toggle hide/show to remove any tab."
        items={categories}
        onChange={setCategories}
        testId="category"
      />
      <FilterGroup
        title="Row 2 — Moods"
        subtitle="Shown on the homepage second row. Drives the 'collection' filter on products."
        items={moods}
        onChange={setMoods}
        testId="mood"
      />
      <button onClick={save} disabled={saving} className="btn-gold" data-testid="filter-manager-save">
        {saving ? "Saving…" : "Save filter order"}
      </button>
    </div>
  );
}

function FilterGroup({ title, subtitle, items, onChange, testId }) {
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...items];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const moveDown = (i) => {
    if (i === items.length - 1) return;
    const next = [...items];
    [next[i + 1], next[i]] = [next[i], next[i + 1]];
    onChange(next);
  };
  const toggleVisible = (i) => {
    const next = [...items];
    next[i] = { ...next[i], visible: !next[i].visible };
    onChange(next);
  };
  const updateLabel = (i, label) => {
    const next = [...items];
    next[i] = { ...next[i], label };
    onChange(next);
  };

  return (
    <section>
      <h3 className="font-serif-display" style={{ fontSize: 22, color: "#FFFFFF" }}>{title}</h3>
      <p className="font-body mt-2 mb-4" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>{subtitle}</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div
            key={`${testId}-${it.value}`}
            data-testid={`filter-row-${testId}-${i}`}
            className="flex items-center gap-3 border border-[#262626] px-3 py-2.5"
            style={{ background: it.visible === false ? "transparent" : "rgba(245,213,101,0.03)" }}
          >
            <div className="flex flex-col">
              <button onClick={() => moveUp(i)} disabled={i === 0}
                      className="text-[10px] px-1 text-[#A3A3A3] hover:text-[#F5D565] disabled:opacity-30"
                      data-testid={`up-${testId}-${i}`}>▲</button>
              <button onClick={() => moveDown(i)} disabled={i === items.length - 1}
                      className="text-[10px] px-1 text-[#A3A3A3] hover:text-[#F5D565] disabled:opacity-30"
                      data-testid={`down-${testId}-${i}`}>▼</button>
            </div>
            <span className="font-ui" style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: "0.15em", textTransform: "uppercase", width: 110 }}>
              {it.value}
            </span>
            <input
              value={it.label}
              onChange={(e) => updateLabel(i, e.target.value)}
              className="flex-1 bg-transparent border-b border-[#262626] py-1.5 text-sm focus:border-[#F5D565] outline-none font-body"
              style={{ color: "#FFFFFF" }}
              data-testid={`label-${testId}-${i}`}
            />
            <button
              onClick={() => toggleVisible(i)}
              data-testid={`visible-${testId}-${i}`}
              className="font-ui inline-flex items-center gap-1.5"
              style={{
                fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", textTransform: "uppercase",
                padding: "5px 9px",
                color: it.visible === false ? "#A3A3A3" : "#0A0A0A",
                background: it.visible === false ? "transparent" : "#F5D565",
                border: it.visible === false ? "1px solid #262626" : "1px solid #F5D565",
              }}
            >
              {it.visible === false ? (<><EyeOff size={11} strokeWidth={1.75} /> Hidden</>) : (<><Eye size={11} strokeWidth={1.75} /> Shown</>)}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Coupons ---------- */

const emptyCoupon = {
  code: "",
  discount_type: "percentage",
  discount_value: 10,
  min_order_value: 0,
  max_uses: "",
  is_active: true,
  expiry_date: "",
  description: "",
};

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(emptyCoupon);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => adminListCoupons().then(setCoupons);
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(emptyCoupon); setShowForm(true); };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      code: c.code,
      discount_type: c.discount_type || "percentage",
      discount_value: c.discount_value,
      min_order_value: c.min_order_value || 0,
      max_uses: c.max_uses ?? "",
      is_active: !!c.is_active,
      expiry_date: c.expiry_date || "",
      description: c.description || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      discount_value: Number(form.discount_value) || 0,
      min_order_value: Number(form.min_order_value) || 0,
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      expiry_date: form.expiry_date || null,
    };
    try {
      if (editing) {
        const { code, ...update } = payload;
        await adminUpdateCoupon(editing, update);
        toast.success("Coupon updated");
      } else {
        await adminCreateCoupon(payload);
        toast.success("Coupon created");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const toggleActive = async (c) => {
    try {
      await adminUpdateCoupon(c.id, { is_active: !c.is_active });
      load();
    } catch {
      toast.error("Couldn't toggle");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await adminDeleteCoupon(c.id);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="mt-8" data-testid="coupons-tab">
      <button onClick={startNew} className="btn-gold mb-6" data-testid="admin-new-coupon">
        <Plus size={14} /> New Coupon
      </button>
      <div className="overflow-x-auto border border-[#262626]">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="bg-[#141414] font-ui" style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Value</th>
              <th className="text-left p-3">Min order</th>
              <th className="text-left p-3">Uses</th>
              <th className="text-left p-3">Active</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="font-body" style={{ fontSize: 14 }}>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-[#1a1a1a]" data-testid={`coupon-row-${c.code}`}>
                <td className="p-3">
                  <span className="font-serif-display" style={{ fontSize: 16, color: "#F5D565" }}>{c.code}</span>
                  {c.description && <div className="text-[11px] text-[#A3A3A3] mt-0.5">{c.description}</div>}
                </td>
                <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>{c.discount_type}</td>
                <td className="p-3">{c.discount_type === "percentage" ? `${c.discount_value}%` : formatINR(c.discount_value)}</td>
                <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>{formatINR(c.min_order_value || 0)}</td>
                <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>
                  {c.uses_so_far || 0}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}
                </td>
                <td className="p-3">
                  <button onClick={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`}
                          className="font-ui inline-flex items-center"
                          style={{
                            fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600,
                            padding: "5px 9px",
                            color: c.is_active ? "#0A0A0A" : "#A3A3A3",
                            background: c.is_active ? "#F5D565" : "transparent",
                            border: "1px solid " + (c.is_active ? "#F5D565" : "#262626"),
                          }}>
                    {c.is_active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => startEdit(c)} className="hover:text-[#F5D565]" data-testid={`coupon-edit-${c.code}`}>
                    <Edit3 size={16} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(c)} className="hover:text-red-400" data-testid={`coupon-delete-${c.code}`}>
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <form onSubmit={submit} data-testid="coupon-form"
            className="bg-[#0A0A0A] border border-[#262626] w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 md:p-8 relative">
            <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]"><X size={20} /></button>
            <h2 className="font-serif-display mb-6" style={{ fontSize: 26, color: "#FFFFFF" }}>{editing ? "Edit" : "New"} Coupon</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Code *" v={form.code} on={(v) => setForm((f) => ({ ...f, code: v.toUpperCase() }))} full />
              <label className="block">
                <Label>Type *</Label>
                <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                        className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none" style={{ fontSize: 15, color: "#FFFFFF" }}>
                  <option value="percentage" className="bg-[#0A0A0A]">Percentage</option>
                  <option value="fixed" className="bg-[#0A0A0A]">Fixed (₹)</option>
                </select>
              </label>
              <Field label={`Value ${form.discount_type === "percentage" ? "(%)" : "(₹)"} *`} type="number" v={form.discount_value} on={(v) => setForm((f) => ({ ...f, discount_value: v }))} />
              <Field label="Min order value (₹)" type="number" v={form.min_order_value} on={(v) => setForm((f) => ({ ...f, min_order_value: v }))} />
              <Field label="Max uses (blank = unlimited)" type="number" v={form.max_uses} on={(v) => setForm((f) => ({ ...f, max_uses: v }))} />
              <Field label="Expiry date" type="date" v={form.expiry_date ? form.expiry_date.slice(0, 10) : ""} on={(v) => setForm((f) => ({ ...f, expiry_date: v }))} />
              <Field label="Description (shown to customer)" v={form.description} on={(v) => setForm((f) => ({ ...f, description: v }))} full />
              <label className="flex items-center gap-2 mt-2 md:col-span-2">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="accent-[#F5D565]" />
                <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>Active</span>
              </label>
            </div>
            <button type="submit" className="btn-gold mt-8 w-full" data-testid="coupon-form-submit">{editing ? "Update" : "Create"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---------- Sort Manager: manual product order ---------- */

function SortManagerTab() {
  const [products, setProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => fetchProducts({ sort: "catalogue" }).then((p) => {
    setProducts(p);
    setDirty(false);
  });
  useEffect(() => { load(); }, []);

  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= products.length) return;
    const next = [...products];
    [next[idx], next[j]] = [next[j], next[idx]];
    setProducts(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminReorderProducts(products.map((p) => p.id));
      toast.success("Order saved. Frontend updates immediately.");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8" data-testid="sort-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="font-body" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", maxWidth: 580 }}>
          Drag to reorder. Position 1 = first on homepage <em>Edit</em> section and Catalogue (when sort = Catalogue).
          Newly added products with no manual order will fall to the back, ordered by newest.
        </p>
        <button onClick={save} disabled={!dirty || saving} className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="sort-manager-save">
          {saving ? "Saving…" : dirty ? "Save order" : "Saved"}
        </button>
      </div>
      <div className="border border-[#262626]">
        {products.map((p, i) => (
          <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-[#1a1a1a]" data-testid={`sort-row-${i}`}>
            <span className="font-ui" style={{ fontSize: 11, color: "#F5D565", letterSpacing: "0.15em", width: 28, textAlign: "center" }}>
              {i + 1}
            </span>
            <GripVertical size={14} className="text-[#A3A3A3]" />
            <img src={resolveMediaUrl(p.images?.[0])} alt="" className="w-10 h-12 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-serif-display truncate" style={{ fontSize: 16, color: "#FFFFFF" }}>{p.name}</div>
              <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }}>{p.category} · {formatINR(p.price)}</div>
            </div>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="text-[10px] px-1 text-[#A3A3A3] hover:text-[#F5D565] disabled:opacity-30"
                data-testid={`sort-up-${i}`}>▲</button>
              <button onClick={() => move(i, 1)} disabled={i === products.length - 1}
                className="text-[10px] px-1 text-[#A3A3A3] hover:text-[#F5D565] disabled:opacity-30"
                data-testid={`sort-down-${i}`}>▼</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Reissue Requests ---------- */

function ReissueTab() {
  const [items, setItems] = useState([]);
  const load = () => adminListReissueRequests().then(setItems);
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateReissue(id, status);
      toast.success("Updated");
      load();
    } catch {
      toast.error("Couldn't update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await adminDeleteReissue(id);
    toast.success("Deleted");
    load();
  };

  const exportCsv = () => {
    if (!items.length) return;
    const header = ["Date", "Product", "Email", "Phone", "Status"];
    const rows = items.map((r) => [
      new Date(r.created_at).toLocaleString("en-IN"),
      r.product_name || r.product_id,
      r.email,
      r.phone || "",
      r.status,
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reissue-requests-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group by product (for "Email All" UX)
  const grouped = items.reduce((acc, r) => {
    const key = r.product_id;
    if (!acc[key]) acc[key] = { product_name: r.product_name || r.product_id, product_id: r.product_id, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});

  return (
    <div className="mt-8" data-testid="reissue-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="font-body" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
          Customers who asked you to reissue a sold-out piece. When you increase stock from 0 → 1+, all <em>new</em> requests for that product are auto-emailed.
        </p>
        <button onClick={exportCsv} disabled={!items.length} className="btn-gold disabled:opacity-40" data-testid="reissue-export-csv">
          Export CSV
        </button>
      </div>
      {items.length === 0 ? (
        <p className="font-serif-display italic mt-6" style={{ fontSize: 22, color: "rgba(255,255,255,0.6)" }}>No reissue requests yet.</p>
      ) : (
        <>
          {/* Product-grouped summary */}
          <div className="mb-8 space-y-2" data-testid="reissue-grouped">
            {Object.values(grouped).map((g) => (
              <div key={g.product_id} className="flex items-center justify-between border border-[#1a1a1a] px-3 py-2.5">
                <span className="font-serif-display" style={{ fontSize: 16, color: "#FFFFFF" }}>{g.product_name}</span>
                <span className="font-ui" style={{ fontSize: 11, color: "#F5D565", letterSpacing: "0.15em" }}>
                  {g.items.length} request{g.items.length > 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>

          {/* Full table */}
          <div className="overflow-x-auto border border-[#262626]" data-testid="reissue-table">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] font-ui" style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="font-body" style={{ fontSize: 14 }}>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-[#1a1a1a]" data-testid={`reissue-row-${r.id}`}>
                    <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>
                      {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="p-3" style={{ color: "#FFFFFF" }}>{r.product_name}</td>
                    <td className="p-3" style={{ color: "rgba(255,255,255,0.85)" }}>{r.email}</td>
                    <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>{r.phone || "—"}</td>
                    <td className="p-3">
                      <select
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        data-testid={`reissue-status-${r.id}`}
                        className="bg-transparent border border-[#262626] py-1.5 px-2 text-xs focus:border-[#F5D565] outline-none font-body"
                        style={{ color: "#FFFFFF" }}
                      >
                        <option value="new" className="bg-[#0A0A0A]">New</option>
                        <option value="contacted" className="bg-[#0A0A0A]">Contacted</option>
                        <option value="fulfilled" className="bg-[#0A0A0A]">Fulfilled</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <button onClick={() => remove(r.id)} className="hover:text-red-400" data-testid={`reissue-delete-${r.id}`}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Policies (Privacy / Shipping / Returns / Terms) ---------- */

const POLICY_TABS = [
  { slug: "privacy", label: "Privacy" },
  { slug: "shipping", label: "Shipping" },
  { slug: "returns", label: "Returns" },
  { slug: "terms", label: "Terms" },
];

function PoliciesTab() {
  const [active, setActive] = useState("privacy");
  const [data, setData] = useState({});
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async (slug) => {
    const r = await api.get(`/policies/${slug}`);
    setData((d) => ({ ...d, [slug]: r.data }));
    setDirty(false);
    try {
      const v = await api.get(`/admin/policies/${slug}/versions`);
      setVersions(v.data || []);
    } catch {
      setVersions([]);
    }
  };

  useEffect(() => { load(active); /* eslint-disable-next-line */ }, [active]);

  const onChange = (slug, field, value) => {
    setData((d) => ({ ...d, [slug]: { ...(d[slug] || {}), [field]: value } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = data[active] || {};
      const r = await api.put(`/admin/policies/${active}`, {
        title: payload.title || "",
        content: payload.content || "",
        meta_title: payload.meta_title || "",
        meta_description: payload.meta_description || "",
      });
      setData((d) => ({ ...d, [active]: r.data }));
      setDirty(false);
      const v = await api.get(`/admin/policies/${active}/versions`);
      setVersions(v.data || []);
      toast.success(`Saved ${active}. Live on /${active} now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const rollback = async () => {
    if (!versions.length) {
      toast.error("No previous version to rollback to");
      return;
    }
    if (!window.confirm("Rollback to the most recent saved version? Current content will be replaced.")) return;
    try {
      const r = await api.post(`/admin/policies/${active}/rollback`);
      setData((d) => ({ ...d, [active]: r.data }));
      const v = await api.get(`/admin/policies/${active}/versions`);
      setVersions(v.data || []);
      toast.success("Rolled back to previous version");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  const current = data[active] || {};

  return (
    <div className="mt-8" data-testid="policies-tab">
      {/* Tabs */}
      <div className="flex flex-wrap gap-0 border border-[#262626] mb-6">
        {POLICY_TABS.map((p) => (
          <button
            key={p.slug}
            onClick={() => setActive(p.slug)}
            data-testid={`policy-tab-${p.slug}`}
            className="font-ui transition-all"
            style={{
              flex: 1,
              padding: "12px 14px",
              fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
              color: active === p.slug ? "#F5D565" : "rgba(255,255,255,0.6)",
              background: active === p.slug ? "rgba(245,213,101,0.06)" : "transparent",
              borderBottom: active === p.slug ? "2px solid #F5D565" : "2px solid transparent",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        <Field label="Page Title" v={current.title || ""} on={(v) => onChange(active, "title", v)} full />
        <div>
          <Label>Content</Label>
          <div className="mt-2">
            <RichTextEditor
              value={current.content || ""}
              onChange={(html) => onChange(active, "content", html)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SEO · Meta title" v={current.meta_title || ""} on={(v) => onChange(active, "meta_title", v)} full />
          <Field label="SEO · Meta description" v={current.meta_description || ""} on={(v) => onChange(active, "meta_description", v)} full />
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button onClick={save} disabled={!dirty || saving} className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="policy-save">
            {saving ? "Saving…" : dirty ? `Save & publish to /${active}` : "Saved"}
          </button>
          <button onClick={rollback} disabled={!versions.length} className="font-ui inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", padding: "10px 14px", color: "#F5D565", border: "1px solid #262626" }}
            data-testid="policy-rollback">
            <RotateCcw size={12} strokeWidth={1.75} /> Rollback ({versions.length})
          </button>
          <a href={`/${active}`} target="_blank" rel="noopener noreferrer"
            className="font-ui" data-testid="policy-preview-link"
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
            Open /{active} ↗
          </a>
        </div>

        {versions.length > 0 && (
          <div className="mt-6 border border-[#1a1a1a] p-4" data-testid="policy-versions">
            <div className="text-[11px] tracking-[0.25em] uppercase" style={{ color: "rgba(255,255,255,0.6)" }}>Version history (last 3)</div>
            <ul className="mt-3 space-y-1">
              {versions.map((v, i) => (
                <li key={i} className="font-body text-[12px]" style={{ color: "rgba(255,255,255,0.75)" }}>
                  · {new Date(v.created_at).toLocaleString("en-IN")}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Small helpers ---------- */

const Label = ({ children }) => (
  <span className="font-ui" style={{ fontSize: 11, fontWeight: 600, color: "#F5D565", letterSpacing: "0.2em", textTransform: "uppercase" }}>
    {children}
  </span>
);

const Field = ({ label, v, on, full = false, type = "text", textarea = false }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <Label>{label}</Label>
    {textarea ? (
      <textarea value={v} onChange={(e) => on(e.target.value)} rows={3}
        className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none resize-none font-body"
        style={{ fontSize: 15, color: "#FFFFFF" }} />
    ) : (
      <input type={type} value={v} onChange={(e) => on(e.target.value)}
        className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none font-body"
        style={{ fontSize: 15, color: "#FFFFFF" }} />
    )}
  </label>
);


/* ---------- Rank inline editor ---------- */

function RankInput({ productId, initial, onSaved }) {
  const [val, setVal] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(initial ?? ""); }, [initial]);

  const commit = async () => {
    const raw = String(val).trim();
    const parsed = raw === "" ? null : Number(raw);
    if (parsed !== null && (isNaN(parsed) || parsed < 0)) {
      toast.error("Rank must be a positive number or blank");
      setVal(initial ?? "");
      return;
    }
    if (parsed === (initial ?? null)) return;
    setBusy(true);
    try {
      await updateProduct(productId, { sort_order: parsed });
      toast.success("Rank saved");
      onSaved?.();
    } catch {
      toast.error("Couldn't save rank");
      setVal(initial ?? "");
    } finally {
      setBusy(false);
    }
  };

  return (
    <input
      type="number"
      min="0"
      value={val}
      disabled={busy}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={`rank-input-${productId}`}
      placeholder="—"
      className="w-16 bg-transparent border border-[#262626] focus:border-[#F5D565] outline-none px-2 py-1 text-center font-body"
      style={{ fontSize: 14, color: "#F5D565" }}
    />
  );
}

/* ---------- Bundle Offers ---------- */

const BUNDLE_COLLECTIONS = ["Daily Luxe", "Date Glam", "9 to 5", "Party Edit", "Big Moments", "Gifts for Her"];

const emptyBundle = {
  name: "", collection: "Daily Luxe", tiers: [{ qty: 1, price: 0 }],
  display_text: "", auto_apply: true, is_active: true, start_at: "", end_at: "",
};

function BundleOffersTab() {
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBundle);
  const [showForm, setShowForm] = useState(false);

  const load = () => adminListBundleOffers().then(setOffers).catch(() => setOffers([]));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(emptyBundle); setShowForm(true); };
  const startEdit = (o) => {
    setEditing(o.id);
    setForm({
      name: o.name || "",
      collection: o.collection || "Daily Luxe",
      tiers: (o.tiers && o.tiers.length ? o.tiers : [{ qty: 1, price: 0 }]).map((t) => ({ qty: t.qty, price: t.price })),
      display_text: o.display_text || "",
      auto_apply: o.auto_apply !== false,
      is_active: o.is_active !== false,
      start_at: o.start_at || "",
      end_at: o.end_at || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tiers: form.tiers
        .map((t) => ({ qty: Number(t.qty), price: Number(t.price) }))
        .filter((t) => t.qty > 0 && t.price > 0)
        .sort((a, b) => a.qty - b.qty),
      start_at: form.start_at || null,
      end_at: form.end_at || null,
    };
    if (payload.tiers.length === 0) {
      toast.error("Add at least one tier");
      return;
    }
    try {
      if (editing) await adminUpdateBundleOffer(editing, payload);
      else await adminCreateBundleOffer(payload);
      toast.success(editing ? "Offer updated" : "Offer created");
      setShowForm(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to save offer");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bundle offer?")) return;
    await adminDeleteBundleOffer(id);
    toast.success("Deleted");
    load();
  };

  const togglePause = async (o) => {
    await adminUpdateBundleOffer(o.id, { is_active: !o.is_active });
    load();
  };

  const updateTier = (i, key, v) => {
    setForm((f) => {
      const tiers = [...f.tiers];
      tiers[i] = { ...tiers[i], [key]: v };
      return { ...f, tiers };
    });
  };

  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { qty: (f.tiers.at(-1)?.qty || 0) + 1, price: 0 }] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  return (
    <div className="mt-8" data-testid="admin-bundles-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display" style={{ fontSize: 24, color: "#FFFFFF" }}>Bundle Offers</h2>
          <p className="font-body mt-1" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
            Multi-buy discounts scoped to a specific collection. Auto-applies in cart.
          </p>
        </div>
        <button onClick={startNew} className="btn-gold" data-testid="admin-new-bundle">
          <Plus size={14} /> New Bundle Offer
        </button>
      </div>

      {offers.length === 0 ? (
        <div className="border border-[#262626] p-8 text-center font-body" style={{ color: "rgba(255,255,255,0.6)" }}>
          No bundle offers yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#262626]">
          <table className="w-full text-sm" data-testid="bundles-table">
            <thead className="bg-[#141414] font-ui" style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Collection</th>
                <th className="text-left p-3">Tiers</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="font-body" style={{ fontSize: 14 }}>
              {offers.map((o) => (
                <tr key={o.id} className="border-t border-[#1a1a1a]" data-testid={`bundle-row-${o.id}`}>
                  <td className="p-3 font-serif-display" style={{ fontSize: 15 }}>{o.name}</td>
                  <td className="p-3" style={{ color: "rgba(255,255,255,0.7)" }}>{o.collection}</td>
                  <td className="p-3" style={{ color: "rgba(255,255,255,0.85)" }}>
                    {(o.tiers || []).map((t, i) => (
                      <span key={i} className="mr-3">{t.qty} for {formatINR(t.price)}</span>
                    ))}
                  </td>
                  <td className="p-3">
                    <span className="font-ui" style={{
                      fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700,
                      color: o.is_active ? "#3E7A4A" : "#A3A3A3",
                    }}>
                      {o.is_active ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td className="p-3 flex gap-3">
                    <button onClick={() => togglePause(o)} className="hover:text-[#F5D565]" data-testid={`bundle-toggle-${o.id}`}>
                      {o.is_active ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                    <button onClick={() => startEdit(o)} className="hover:text-[#F5D565]" data-testid={`bundle-edit-${o.id}`}>
                      <Edit3 size={16} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(o.id)} className="hover:text-red-400" data-testid={`bundle-delete-${o.id}`}>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <form onSubmit={submit} data-testid="bundle-form"
            className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8 relative">
            <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]">
              <X size={20} />
            </button>
            <h2 className="font-serif-display mb-6" style={{ fontSize: 28, color: "#FFFFFF" }}>
              {editing ? "Edit" : "New"} Bundle Offer
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name *" v={form.name} on={(v) => setForm((f) => ({ ...f, name: v }))} full />
              <label className="block">
                <Label>Collection *</Label>
                <select
                  value={form.collection}
                  onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))}
                  className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 focus:border-[#F5D565] outline-none"
                  style={{ fontSize: 15, color: "#FFFFFF" }}
                  data-testid="bundle-collection"
                >
                  {BUNDLE_COLLECTIONS.map((c) => (
                    <option key={c} value={c} className="bg-[#0A0A0A]">{c}</option>
                  ))}
                </select>
              </label>
              <Field label="Display Text" v={form.display_text} on={(v) => setForm((f) => ({ ...f, display_text: v }))} full />

              <div className="md:col-span-2">
                <Label>Tiers (qty → price)</Label>
                <div className="mt-3 space-y-2" data-testid="bundle-tiers">
                  {form.tiers.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={t.qty}
                        onChange={(e) => updateTier(i, "qty", e.target.value)}
                        className="w-24 bg-transparent border border-[#262626] focus:border-[#F5D565] outline-none px-3 py-2 font-body"
                        style={{ fontSize: 14, color: "#FFFFFF" }}
                        placeholder="Qty"
                        data-testid={`bundle-tier-qty-${i}`}
                      />
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>for</span>
                      <input
                        type="number"
                        min="0"
                        value={t.price}
                        onChange={(e) => updateTier(i, "price", e.target.value)}
                        className="flex-1 bg-transparent border border-[#262626] focus:border-[#F5D565] outline-none px-3 py-2 font-body"
                        style={{ fontSize: 14, color: "#FFFFFF" }}
                        placeholder="Price (₹)"
                        data-testid={`bundle-tier-price-${i}`}
                      />
                      {form.tiers.length > 1 && (
                        <button type="button" onClick={() => removeTier(i)} className="text-[#A3A3A3] hover:text-red-400 p-2">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addTier}
                  data-testid="bundle-add-tier"
                  className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.25em] uppercase text-[#F5D565] hover:opacity-80"
                >
                  <Plus size={12} /> Add tier
                </button>
              </div>

              <Field label="Start Date (optional, ISO)" v={form.start_at} on={(v) => setForm((f) => ({ ...f, start_at: v }))} />
              <Field label="End Date (optional, ISO)" v={form.end_at} on={(v) => setForm((f) => ({ ...f, end_at: v }))} />

              <label className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={form.auto_apply}
                  onChange={(e) => setForm((f) => ({ ...f, auto_apply: e.target.checked }))}
                  className="accent-[#F5D565]"
                  data-testid="bundle-auto-apply"
                />
                <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
                  Auto-apply in cart (no coupon needed)
                </span>
              </label>
              <label className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="accent-[#F5D565]"
                  data-testid="bundle-active"
                />
                <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
                  Active
                </span>
              </label>
            </div>
            <button type="submit" className="btn-gold mt-8 w-full" data-testid="bundle-submit">
              {editing ? "Update Offer" : "Create Offer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
